import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/services/api";
import {
  Sparkles, FileText, GitBranch, Layers, ArrowLeft,
  Loader2, RefreshCw, Wand2, CheckCircle2, AlertCircle,
  Calendar, MapPin, Users, Building2, Target, Network,
  ChevronDown, ChevronUp, Plus, Edit3, Check, X,
  Heart, Camera, Music, UtensilsCrossed, Palette, Truck,
  Star, Zap, Info, Lock, TrendingUp, BarChart3, Crown,
  Eye, ChevronRight, Gem, Hotel, Award, ArrowUpRight,
  GitPullRequest, ShieldAlert, ShieldCheck, ShieldX
} from "lucide-react";
import EventChanges from "@/pages/EventChanges";
import EventReadiness from "@/pages/EventReadiness";
import { toast } from "sonner";

const TABS = ["overview", "brief", "blueprint", "threads", "changes", "360", "readiness"];

export default function EventImagine() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get(`/imagine/events/${eventId}`)
      .then(r => setEvent(r.data))
      .catch(() => toast.error("Failed to load event"))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#0176D3]" /></div>;
  if (!event) return <div className="p-8 text-[#706E6B]">Event not found.</div>;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/imagine")} className="p-2 hover:bg-[#FAFAFA] rounded-lg text-[#706E6B] hover:text-[#181818] transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-[#706E6B]">{event.event_number}</span>
            <StageBadge stage={event.planning_stage} />
          </div>
          <h1 className="text-lg sm:text-xl font-bold text-[#181818] truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {event.name}
          </h1>
        </div>
        <button onClick={load} className="p-2 hover:bg-[#FAFAFA] rounded-lg text-[#706E6B] flex-shrink-0">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-0.5 border-b border-[#E5E5E5] overflow-x-auto">
        {[
          { id: "overview",   label: "Overview",   icon: Layers },
          { id: "brief",      label: "Brief",       icon: FileText },
          { id: "blueprint",  label: "Blueprint",   icon: GitBranch },
          { id: "threads",    label: "Threads",     icon: Network },
          { id: "changes",    label: "Changes",     icon: GitPullRequest },
          { id: "360",        label: "Event 360",   icon: Target },
          { id: "readiness",  label: "Readiness",   icon: ShieldAlert },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`event-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id
                ? "border-[#0176D3] text-[#0176D3]"
                : "border-transparent text-[#706E6B] hover:text-[#444444]"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview"   && <OverviewTab event={event} navigate={navigate} onRefresh={load} />}
      {tab === "brief"      && <BriefTab eventId={eventId} onRefresh={load} />}
      {tab === "blueprint"  && <BlueprintTab eventId={eventId} event={event} onRefresh={load} navigate={navigate} />}
      {tab === "threads"    && (
        <div className="text-center py-12">
          <button data-testid="goto-threads-btn" onClick={() => navigate(`/events/${eventId}/threads`)}
            className="px-6 py-3 bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30 rounded-xl text-sm font-medium hover:bg-[#EEF4FF] transition-colors">
            Open Event Threads →
          </button>
        </div>
      )}
      {tab === "changes"    && <EventChanges eventId={eventId} />}
      {tab === "360"        && <Event360Panel eventId={eventId} event={event} navigate={navigate} />}
      {tab === "readiness"  && <EventReadiness eventId={eventId} />}
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewTab({ event, navigate, onRefresh }) {
  const [activating, setActivating] = useState(false);
  async function handleActivate() {
    setActivating(true);
    try { await api.post(`/imagine/events/${event.id}/activate`); toast.success("Event Graph activated!"); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Activation failed"); }
    finally { setActivating(false); }
  }
  async function handleActivateThreads() {
    setActivating(true);
    try { const r = await api.post(`/events/${event.id}/threads/activate`); toast.success(`${r.data.count} Threads activated!`); navigate(`/events/${event.id}/threads`); }
    catch (e) { toast.error(e.response?.data?.detail || "Thread activation failed"); }
    finally { setActivating(false); }
  }
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Event Type",  value: event.event_type_name, icon: Sparkles },
          { label: "Event Date",  value: event.event_date,      icon: Calendar },
          { label: "Location",    value: event.location_city,   icon: MapPin },
          { label: "Guests",      value: event.guest_count,     icon: Users },
        ].map(item => (
          <div key={item.label} className="bg-white border border-[#E5E5E5] rounded-xl p-4">
            <p className="text-xs text-[#706E6B] uppercase tracking-wide mb-1">{item.label}</p>
            <p className="text-sm font-semibold text-[#181818]">{item.value || "—"}</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
        <h3 className="text-sm font-semibold text-[#444444] mb-4">Planning Journey</h3>
        <PlanningJourney stage={event.planning_stage} threadsActivated={event.threads_activated} />
      </div>
      <div className="flex gap-3 flex-wrap">
        {event.planning_stage === "APPROVED" && !event.threads_activated && (
          <ActionButton label="Activate Event Graph" icon={<CheckCircle2 className="w-4 h-4" />} onClick={handleActivate} loading={activating} variant="primary" testId="activate-event-graph-btn" />
        )}
        {event.planning_stage === "ACTIVATED" && !event.threads_activated && (
          <ActionButton label="Activate Threads" icon={<Network className="w-4 h-4" />} onClick={handleActivateThreads} loading={activating} variant="primary" testId="activate-threads-btn" />
        )}
        {event.threads_activated && (
          <ActionButton label="Open Event Threads" icon={<Network className="w-4 h-4" />} onClick={() => navigate(`/events/${event.id}/threads`)} variant="secondary" testId="open-threads-btn" />
        )}
        <ActionButton label="View Event 360" icon={<Target className="w-4 h-4" />} onClick={() => navigate(`/events/${event.id}/360`)} variant="ghost" testId="view-360-btn" />
      </div>
      {event.source_lead_id && (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
          <p className="text-xs text-[#706E6B] uppercase tracking-wide mb-1">Source Lead</p>
          <p className="text-sm text-[#0176D3] font-mono">{event.source_lead_id}</p>
        </div>
      )}
    </div>
  );
}

// ── Event 360 Panel ───────────────────────────────────────────────────────────
function Event360Panel({ eventId, event, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/events/${eventId}/360`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [eventId]);

  const SEV_STYLE = {
    CRITICAL: "bg-[#FCEBEC] text-[#BA0517]",
    HIGH:     "bg-[#FFF4E5] text-[#8C4B00]",
    MEDIUM:   "bg-[#EEF4FF] text-[#0176D3]",
    LOW:      "bg-[#FAFAFA] text-[#706E6B]",
  };
  const ST_STYLE = {
    PROPOSED:         "bg-[#FAFAFA] text-[#706E6B]",
    IMPACT_ASSESSED:  "bg-[#EEF4FF] text-[#0176D3]",
    AWAITING_DECISION:"bg-[#FFF4E5] text-[#8C4B00]",
    AWAITING_APPROVAL:"bg-[#FFF4E5] text-[#8C4B00]",
    APPROVED:         "bg-[#EAF5EE] text-[#2E844A]",
    IMPLEMENTING:     "bg-[#EEF4FF] text-[#0176D3]",
  };
  const RD_STATE_STYLE = {
    READY:     { bg: "bg-[#EAF5EE]", text: "text-[#2E844A]", border: "border-[#A3D3AE]" },
    ATTENTION: { bg: "bg-[#FFF4E5]", text: "text-[#8C4B00]", border: "border-[#F5C45E]" },
    BLOCKED:   { bg: "bg-[#FCEBEC]", text: "text-[#BA0517]", border: "border-[#F59DAB]" },
    NOT_READY: { bg: "bg-[#F4F6F8]", text: "text-[#706E6B]", border: "border-[#E5E5E5]" },
  };

  const oc = data?.open_changes;
  const changes = oc?.changes || [];
  const totalOpen = oc?.open_change_count || 0;
  const rp = data?.readiness_projection;

  return (
    <div className="space-y-4" data-testid="event-360-panel">
      {/* Summary header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#181818]">Event 360 — Operational View</h3>
          <p className="text-xs text-[#706E6B] mt-0.5">
            {loading ? "Loading…" : `${totalOpen} open change${totalOpen !== 1 ? "s" : ""} affecting this event`}
          </p>
        </div>
        <button
          onClick={() => navigate(`/events/${eventId}/360`)}
          className="text-xs text-[#0176D3] hover:underline flex items-center gap-1"
          data-testid="goto-full-360-btn"
        >
          Full Event 360 →
        </button>
      </div>

      {/* ── Compact Readiness Projection (EO-02) ── */}
      {rp && (
        <div className={`rounded-xl border p-4 ${RD_STATE_STYLE[rp.overall_state]?.bg} ${RD_STATE_STYLE[rp.overall_state]?.border}`}
          data-testid="readiness-projection-compact">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {rp.overall_state === "READY" ? <ShieldCheck className="w-4 h-4 text-[#2E844A]" />
                : rp.overall_state === "BLOCKED" ? <ShieldX className="w-4 h-4 text-[#BA0517]" />
                : <ShieldAlert className={`w-4 h-4 ${RD_STATE_STYLE[rp.overall_state]?.text}`} />
              }
              <span className="text-xs font-semibold text-[#181818]">Readiness</span>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${RD_STATE_STYLE[rp.overall_state]?.bg} ${RD_STATE_STYLE[rp.overall_state]?.text} border ${RD_STATE_STYLE[rp.overall_state]?.border}`}>
                {rp.overall_state} · {rp.overall_pct}%
              </span>
              {rp.has_critical_path_blockers && (
                <span className="text-[10px] text-[#BA0517] font-medium">Dependency blockers exist</span>
              )}
            </div>
            <button onClick={() => navigate(`/events/${eventId}?tab=readiness`)}
              className="text-[10px] text-[#0176D3] hover:underline">
              Full Readiness →
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {rp.gates?.map(g => {
              const gs = RD_STATE_STYLE[g.state] || RD_STATE_STYLE.NOT_READY;
              return (
                <div key={g.gate} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${gs.bg} ${gs.border}`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    g.state === "READY" ? "bg-[#2E844A]"
                    : g.state === "BLOCKED" ? "bg-[#BA0517]"
                    : g.state === "ATTENTION" ? "bg-[#DD7A00]"
                    : "bg-[#706E6B]"
                  }`} />
                  <span className={`text-[10px] font-semibold ${gs.text}`}>
                    {g.gate === "EVENT_DAY" ? "E-Day" : g.gate.charAt(0) + g.gate.slice(1).toLowerCase()}
                  </span>
                  <span className={`text-[10px] ${gs.text}`}>{g.completion_pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats row */}
      {oc && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Critical / High",  value: (oc.severity_counts?.CRITICAL || 0) + (oc.severity_counts?.HIGH || 0), color: "text-[#BA0517]" },
            { label: "Awaiting Approval", value: changes.reduce((s, c) => s + (c.pending_approval_count || 0), 0), color: "text-[#8C4B00]" },
            { label: "Open Issues",       value: changes.reduce((s, c) => s + (c.open_issue_count || 0), 0), color: "text-[#BA0517]" },
          ].map(m => (
            <div key={m.label} className="bg-white border border-[#E5E5E5] rounded-xl p-3">
              <p className="text-[10px] text-[#706E6B] uppercase tracking-wide mb-0.5">{m.label}</p>
              <p className={`text-xl font-bold ${m.color}`}>{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {loading && <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#0176D3]" /></div>}

      {!loading && changes.length === 0 && (
        <div className="text-center py-12 bg-white border border-[#E5E5E5] rounded-xl">
          <CheckCircle2 className="w-8 h-8 text-[#2E844A] mx-auto mb-2" />
          <p className="text-sm font-medium text-[#181818]">No open changes</p>
          <p className="text-xs text-[#706E6B] mt-1">This event has no open change requests.</p>
        </div>
      )}

      {changes.map((c, i) => (
        <div key={c.id || i} className="bg-white border border-[#E5E5E5] rounded-xl p-4 space-y-3" data-testid={`360-change-${c.id}`}>
          {/* Change header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono text-[#706E6B]">{c.change_number}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${SEV_STYLE[c.severity] || ""}`}>{c.severity}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${ST_STYLE[c.status] || ""}`}>{c.status?.replace(/_/g, " ")}</span>
              </div>
              <p className="text-sm font-medium text-[#181818] truncate">{c.change_field?.replace(/_/g, " ")} — {c.description?.slice(0, 60)}{(c.description?.length || 0) > 60 ? "…" : ""}</p>
            </div>
          </div>

          {/* Impact stats */}
          <div className="flex items-center gap-4 text-[10px] text-[#706E6B]">
            {(c.impact_count || 0) > 0 && <span><span className="font-medium text-[#444444]">{c.impact_count}</span> impacted areas</span>}
            {(c.open_task_count || 0) > 0 && <span><span className="font-medium text-[#444444]">{c.open_task_count}</span> open tasks</span>}
            {(c.pending_approval_count || 0) > 0 && (
              <span className="text-[#8C4B00] font-medium">
                {c.pending_approval_count} pending approval{c.pending_approval_count !== 1 ? "s" : ""}
              </span>
            )}
            {(c.open_issue_count || 0) > 0 && (
              <span className="text-[#BA0517] font-medium">
                {c.open_issue_count} open issue{c.open_issue_count !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* CTAs */}
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/events/${eventId}/changes/${c.id}`)}
              className="px-3 py-1.5 text-xs bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/20 rounded-lg font-medium hover:opacity-90"
              data-testid={`open-change-${c.id}`}
            >
              Open Change
            </button>
            {(c.pending_approval_count || 0) > 0 && (
              <button
                onClick={() => navigate(`/events/${eventId}/changes/${c.id}`, { state: { section: "approvals" } })}
                className="px-3 py-1.5 text-xs bg-[#FFF4E5] text-[#8C4B00] border border-[#FE9339]/30 rounded-lg font-medium hover:opacity-90"
                data-testid={`review-approval-${c.id}`}
              >
                Review Approval
              </button>
            )}
          </div>
        </div>
      ))}

      {totalOpen > 5 && (
        <button
          onClick={() => navigate(`/events/${eventId}/360`)}
          className="w-full py-2 text-xs text-[#0176D3] hover:underline text-center"
          data-testid="view-all-360-btn"
        >
          View all {totalOpen} changes in Event 360 →
        </button>
      )}
    </div>
  );
}

// ── Brief Tab ─────────────────────────────────────────────────────────────────
function BriefTab({ eventId, onRefresh }) {  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interpreting, setInterpreting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => {
    api.get(`/imagine/events/${eventId}/brief`)
      .then(r => { setBrief(r.data); setForm(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId]);
  async function handleInterpret() {
    setInterpreting(true);
    try { const r = await api.post(`/imagine/events/${eventId}/brief/interpret`); setBrief(r.data); setForm(r.data); toast.success("AI Brief interpretation complete"); }
    catch (e) { toast.error(e.response?.data?.detail || "Interpretation failed"); }
    finally { setInterpreting(false); }
  }
  async function handleSave() {
    try { const r = await api.put(`/imagine/events/${eventId}/brief`, form); setBrief(r.data); setEditing(false); onRefresh(); toast.success("Brief updated"); }
    catch { toast.error("Failed to save Brief"); }
  }
  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-[#0176D3]" /></div>;
  if (!brief) return <div className="text-[#706E6B] text-sm py-8 text-center">Brief not found.</div>;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${brief.validation_status === "VALIDATED" ? "bg-[#EAF5EE] text-[#2E844A]" : brief.validation_status === "COMPLETE" ? "bg-[#EEF4FF] text-[#0176D3]" : "bg-[#FAFAFA] text-[#706E6B]"}`}>
            {brief.validation_status || "DRAFT"}
          </span>
          {brief.ai_interpreted && <span className="text-xs text-[#5E4DB2] flex items-center gap-1"><Wand2 className="w-3 h-3" />AI interpreted ({Math.round((brief.ai_confidence || 0) * 100)}% confidence)</span>}
        </div>
        <div className="flex gap-2">
          <button data-testid="interpret-brief-btn" onClick={handleInterpret} disabled={interpreting} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#F1ECFF] text-[#5E4DB2] border border-[#D8CEF7] rounded-lg hover:bg-[#E8E0FF] transition-colors disabled:opacity-50">
            {interpreting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}AI Interpret
          </button>
          <button data-testid="edit-brief-btn" onClick={() => setEditing(!editing)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#FAFAFA] text-[#444444] rounded-lg hover:bg-[#E5E5E5] transition-colors">
            <Edit3 className="w-3 h-3" />{editing ? "Cancel" : "Edit"}
          </button>
          {editing && <button onClick={handleSave} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#0176D3] text-white rounded-lg hover:opacity-90"><Check className="w-3 h-3" />Save</button>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <BriefSection title="Event Basics">
          <BriefRow label="Event Type" field="event_type" form={form} setForm={setForm} editing={editing} />
          <BriefRow label="Date" field="event_date" form={form} setForm={setForm} editing={editing} type="date" />
          <BriefRow label="Date Confirmed" field="date_confirmed" form={form} setForm={setForm} editing={editing} type="bool" />
          <BriefRow label="Venue Status" field="venue_status" form={form} setForm={setForm} editing={editing} />
          <BriefRow label="Venue" field="venue_name" form={form} setForm={setForm} editing={editing} />
        </BriefSection>
        <BriefSection title="Location & Guests">
          <BriefRow label="City" field="location_city" form={form} setForm={setForm} editing={editing} />
          <BriefRow label="Guest Count" field="guest_count" form={form} setForm={setForm} editing={editing} type="number" />
          <BriefRow label="Indoor/Outdoor" field="indoor_outdoor" form={form} setForm={setForm} editing={editing} />
          <BriefRow label="Food Preference" field="food_preference" form={form} setForm={setForm} editing={editing} />
        </BriefSection>
        <BriefSection title="Style & Theme">
          <BriefRow label="Theme" field="theme" form={form} setForm={setForm} editing={editing} />
          <BriefRow label="Style" field="style" form={form} setForm={setForm} editing={editing} />
        </BriefSection>
        <BriefSection title="Services Required">
          {[["Decor","decor_needed"],["Photography","photography_needed"],["Entertainment","entertainment_needed"],["Accommodation","accommodation_needed"],["Transport","transport_needed"],["Hospitality","hospitality_needed"],["Lighting","lighting_needed"]].map(([label, field]) => (
            <BriefRow key={field} label={label} field={field} form={form} setForm={setForm} editing={editing} type="bool" />
          ))}
        </BriefSection>
      </div>
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
        <p className="text-xs text-[#706E6B] uppercase tracking-wide mb-2">Planning Notes</p>
        {editing ? (
          <textarea rows={3} value={form.poc_notes || ""} onChange={e => setForm(f => ({ ...f, poc_notes: e.target.value }))} className="w-full bg-white border border-[#C9C9C9] rounded-lg text-sm text-[#181818] p-2 resize-none focus:ring-1 focus:ring-[#0176D3] outline-none" />
        ) : <p className="text-sm text-[#444444]">{brief.poc_notes || "—"}</p>}
      </div>
      {brief.missing_fields?.length > 0 && (
        <div className="flex items-start gap-2 p-3 bg-[#FFF4E5] border border-[#FE9339]/30 rounded-xl">
          <AlertCircle className="w-4 h-4 text-[#8C4B00] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-[#8C4B00]">Missing Information</p>
            <p className="text-xs text-amber-500 mt-0.5">{brief.missing_fields.join(", ")}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Blueprint Tab — PREMIUM VISUAL EXPERIENCE ─────────────────────────────────
function BlueprintTab({ eventId, event, onRefresh, navigate }) {
  const [bp, setBp] = useState(null);
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [expandedFns, setExpandedFns] = useState({});
  const [expandedSvcs, setExpandedSvcs] = useState({});

  useEffect(() => {
    Promise.all([
      api.get(`/imagine/events/${eventId}/blueprint`).catch(() => ({ data: null })),
      api.get(`/imagine/events/${eventId}/brief`).catch(() => ({ data: null })),
    ]).then(([bpR, brR]) => {
      setBp(bpR.data);
      setBrief(brR.data);
    }).finally(() => setLoading(false));
  }, [eventId]);

  async function handleGenerate() {
    setGenerating(true);
    try {
      const r = await api.post(`/imagine/events/${eventId}/blueprint/generate`);
      setBp(r.data);
      onRefresh();
      toast.success("Blueprint generated by AI");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Blueprint generation failed");
    } finally { setGenerating(false); }
  }

  async function handleApprove() {
    if (!bp) return;
    setApproving(true);
    try {
      const r = await api.post(`/imagine/events/${eventId}/blueprint/${bp.id}/approve`, { poc_notes: "" });
      setBp(r.data);
      onRefresh();
      toast.success("Blueprint approved!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally { setApproving(false); }
  }

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-[#0176D3]" /></div>;

  if (!bp) {
    return (
      <div className="rounded-2xl border border-[#E5E5E5] bg-white p-10 sm:p-16 text-center space-y-6">
        <div className="w-16 h-16 rounded-2xl bg-[#F1ECFF] border border-[#D8CEF7] flex items-center justify-center mx-auto">
          <GitBranch className="w-8 h-8 text-[#5E4DB2]" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#181818] font-sans">No Blueprint Yet</h3>
          <p className="text-sm text-[#706E6B] mt-1">Generate an AI-crafted Event Blueprint from the completed Brief.</p>
        </div>
        <button data-testid="generate-blueprint-btn" onClick={handleGenerate} disabled={generating}
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#0176D3] text-white rounded-xl text-sm font-medium hover:bg-[#0176D3]/90 transition-colors disabled:opacity-50 mx-auto">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          Generate Blueprint with AI
        </button>
      </div>
    );
  }

  const coverage = calcCoverage(bp);

  return (
    <div className="space-y-5" data-testid="blueprint-tab-content">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <BlueprintHero bp={bp} event={event} brief={brief} coverage={coverage} />

      {/* ── AGENT SUMMARY ────────────────────────────────────────────────── */}
      {bp.agent_summary && <AgentSummaryCard bp={bp} brief={brief} />}

      {/* ── INTELLIGENCE STRIP ───────────────────────────────────────────── */}
      <IntelligenceStrip bp={bp} event={event} brief={brief} />

      {/* ── EVENT FUNCTIONS ──────────────────────────────────────────────── */}
      {(bp.event_functions || []).length > 0 && (
        <section data-testid="blueprint-functions-section">
          <SectionHeader
            icon={<Sparkles className="w-4 h-4 text-[#0176D3]" />}
            title="Event Functions"
            subtitle="Core functions for this Event, managed end-to-end."
          />
          <div className="space-y-2 mt-3">
            {bp.event_functions.map((fn, i) => (
              <FunctionCard
                key={fn.code || i}
                fn={fn}
                index={i}
                expanded={expandedFns[fn.code || i]}
                onToggle={() => setExpandedFns(p => ({ ...p, [fn.code || i]: !p[fn.code || i] }))}
                services={(bp.event_services || []).filter(s => s.applies_to_functions?.includes(fn.code))}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── SERVICE DECOMPOSITION ────────────────────────────────────────── */}
      {(bp.event_services || []).length > 0 && (
        <section data-testid="blueprint-services-section">
          <SectionHeader
            icon={<BarChart3 className="w-4 h-4 text-[#6832B8]" />}
            title="Service Decomposition"
            subtitle="Services derived from customer requirements and Event needs."
          />
          <div className="space-y-2 mt-3">
            {bp.event_services.map((svc, i) => (
              <ServiceRow
                key={svc.code || i}
                svc={svc}
                expanded={expandedSvcs[svc.code || i]}
                onToggle={() => setExpandedSvcs(p => ({ ...p, [svc.code || i]: !p[svc.code || i] }))}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── MISSING INFORMATION ──────────────────────────────────────────── */}
      {(bp.missing_information || []).length > 0 && (
        <MissingInfoCard items={bp.missing_information} eventId={eventId} navigate={navigate} />
      )}

      {/* ── ARCHITECTURE TREE ────────────────────────────────────────────── */}
      {(bp.event_functions || []).length > 0 && (bp.event_services || []).length > 0 && (
        <ArchitectureTree functions={bp.event_functions} services={bp.event_services} />
      )}

      {/* ── POC ACTIONS + EVENT GRAPH ────────────────────────────────────── */}
      <BlueprintActionsBar
        bp={bp}
        event={event}
        onGenerate={handleGenerate}
        onApprove={handleApprove}
        generating={generating}
        approving={approving}
        navigate={navigate}
      />

      {bp.approved_by && (
        <div className="text-xs text-[#706E6B] flex items-center gap-1.5 pb-2">
          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
          Approved {bp.approved_at?.slice(0, 10)} · V{bp.version}
          {bp.customer_confirmed && <><span className="text-[#444444] mx-1">·</span><span className="text-emerald-500">Customer confirmed</span></>}
        </div>
      )}
    </div>
  );
}

// ── Coverage formula (deterministic, documented) ──────────────────────────────
// function_coverage  = count(required=true functions) / total functions
// service_coverage   = count(status=REQUIRED|CONFIRMED services) / total active services
// requirement_mapping = ai_confidence (AI-assessed % of brief requirements mapped to services)
// overall            = (fn_score × 0.35 + svc_score × 0.35 + req_mapping × 0.30) × 100
function calcCoverage(bp) {
  const fns  = bp.event_functions  || [];
  const svcs = bp.event_services   || [];

  const totalFn         = fns.length;
  const confirmedFn     = fns.filter(f => f.required).length;
  const fnScore         = totalFn > 0 ? confirmedFn / totalFn : 0;

  const activeSvcs      = svcs.filter(s => s.status !== "EXCLUDED");
  const requiredSvcs    = svcs.filter(s => s.status === "REQUIRED" || s.status === "CONFIRMED").length;
  const svcScore        = activeSvcs.length > 0 ? requiredSvcs / activeSvcs.length : 0;

  const reqMapping      = bp.ai_confidence || 0;
  const overall         = Math.round((fnScore * 0.35 + svcScore * 0.35 + reqMapping * 0.30) * 100);

  return { overall, totalFn, confirmedFn, totalSvc: activeSvcs.length, requiredSvcs, reqMapping: Math.round(reqMapping * 100) };
}

// ── EVENT TYPE visual themes ──────────────────────────────────────────────────
const EVENT_THEMES = {
  Engagement:  { from: "from-[#FFF0F3]",  via: "via-[#FFE8EF]",    to: "to-[#FFF5F8]",   accent: "text-rose-700",   icon: Heart },
  Wedding:     { from: "from-[#FFF4E5]",  via: "via-[#FFF8F0]",    to: "to-[#FFFAF3]",   accent: "text-[#8C4B00]",  icon: Crown },
  Corporate:   { from: "from-[#EAF4FF]",  via: "via-[#F0F8FF]",    to: "to-[#F5F9FF]",   accent: "text-[#0176D3]",  icon: Building2 },
  Birthday:    { from: "from-[#F1ECFF]",  via: "via-[#F6F2FF]",    to: "to-[#FAF8FF]",   accent: "text-[#5E4DB2]",  icon: Star },
  Anniversary: { from: "from-[#FFF0FB]",  via: "via-[#F7EEFF]",    to: "to-[#F5F0FF]",   accent: "text-fuchsia-700",icon: Heart },
};
function getEventTheme(type) {
  return EVENT_THEMES[type] || { from: "from-[#EAF4FF]", via: "via-[#F0F8FF]", to: "to-[#F5F9FF]", accent: "text-[#0176D3]", icon: Sparkles };
}

// ── SERVICE icon map ───────────────────────────────────────────────────────────
const SVC_ICON = {
  'sc-decor':           Palette,
  'sc-catering':        UtensilsCrossed,
  'sc-photography':     Camera,
  'sc-entertainment':   Music,
  'sc-lighting':        Zap,
  'sc-transport':       Truck,
  'sc-hospitality':     Hotel,
  'sc-accommodation':   Hotel,
};
function svcIcon(code) { return SVC_ICON[code] || Award; }

// ── FUNCTION icon + color map ─────────────────────────────────────────────────
const FN_META = {
  'FN-ENGAGEMENT': { icon: Heart,       color: "text-rose-600",   bg: "bg-rose-50 border-rose-200" },
  'FN-WEDDING':    { icon: Crown,       color: "text-[#8C4B00]",  bg: "bg-[#FFF4E5] border-[#FE9339]/30" },
  'FN-COCKTAIL':   { icon: Star,        color: "text-[#6832B8]", bg: "bg-[#F6EEFE] border-[#6832B8]/20" },
  'FN-RECEPTION':  { icon: Users,       color: "text-[#0176D3]",   bg: "bg-[#EEF4FF] border-[#0176D3]/20" },
  'FN-PHOTOS':     { icon: Camera,      color: "text-[#8C4B00]", bg: "bg-[#FFF4E5] border-[#FE9339]/30" },
  'FN-ENTERTAIN':  { icon: Music,       color: "text-[#2E844A]",bg: "bg-[#EAF5EE] border-[#2E844A]/20" },
};
function fnMeta(code) { return FN_META[code] || { icon: Sparkles, color: "text-[#0176D3]", bg: "bg-[#EEF4FF] border-[#0176D3]/30" }; }

// ── Service status config ─────────────────────────────────────────────────────
const STATUS_STYLE = {
  REQUIRED:  { pill: "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30",  dot: "bg-[#0176D3]" },
  CONFIRMED: { pill: "bg-emerald-500/10 text-[#2E844A] border border-emerald-500/25", dot: "bg-emerald-400" },
  PROPOSED:  { pill: "bg-[#F1ECFF] text-[#5E4DB2] border border-[#D8CEF7]",           dot: "bg-[#5E4DB2]" },
  OPTIONAL:  { pill: "bg-[#E5E5E5]/60 text-[#706E6B] border border-[#C9C9C9]",         dot: "bg-slate-500" },
  EXCLUDED:  { pill: "bg-[#FCEBEC] text-[#BA0517]/60 border border-[#BA0517]/20",         dot: "bg-red-500/50" },
};
function statusStyle(s) { return STATUS_STYLE[s] || STATUS_STYLE.OPTIONAL; }

// ══════════════════════════════════════════════════════════════════════════════
// HERO
// ══════════════════════════════════════════════════════════════════════════════
function BlueprintHero({ bp, event, brief, coverage }) {
  const theme = getEventTheme(event.event_type_name);

  const chips = [
    brief?.theme,
    event.guest_count && `~${event.guest_count} Guests`,
    bp.event_functions?.length && `${bp.event_functions.length} Functions`,
    event.event_type_name && `${event.event_type_name} Event`,
    event.event_date && new Date(event.event_date).toLocaleDateString("en-IN", { day: "numeric", month: "long" }),
    event.location_city,
  ].filter(Boolean);

  return (
    <div className="rounded-2xl border border-[#E5E5E5] overflow-hidden bg-white" data-testid="blueprint-hero">
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1.2fr_2fr]">

        {/* ── LEFT: identity ─────────────────────────────────────────────── */}
        <div className="p-5 sm:p-7 flex flex-col gap-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] text-[#706E6B] bg-[#FAFAFA] px-2 py-0.5 rounded border border-[#C9C9C9]">
                V{bp.version}
              </span>
              <BlueprintStatusBadge status={bp.status} />
              {bp.ai_confidence > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-[#5E4DB2] bg-[#F1ECFF] px-2 py-0.5 rounded border border-[#D8CEF7]">
                  <Wand2 className="w-2.5 h-2.5" />{Math.round(bp.ai_confidence * 100)}% confidence
                </span>
              )}
            </div>
            <p className="text-[9px] font-mono text-[#706E6B] uppercase tracking-widest">AI Generated Blueprint</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#181818] leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Event Blueprint
            </h2>
            <p className="text-xs text-[#706E6B] leading-relaxed">
              AI-crafted event plan with structured functions, services and customer requirements.
            </p>
          </div>

          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {chips.map((chip, i) => (
                <span key={i} className="text-[10px] font-medium px-2.5 py-1 rounded-full bg-[#F3F3F3] text-[#706E6B] border border-[#EEF4FF]">
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── CENTER: concept visual ──────────────────────────────────────── */}
        <div className={`relative overflow-hidden min-h-[180px] lg:min-h-0 lg:border-x border-[#E5E5E5] flex items-center justify-center bg-gradient-to-br ${theme.from} ${theme.via} ${theme.to}`}>
          {/* Concentric rings */}
          <div className="absolute w-40 h-40 rounded-full border border-black/5" />
          <div className="absolute w-64 h-64 rounded-full border border-black/[0.03]" />
          <div className="absolute w-96 h-96 rounded-full border border-black/[0.015]" />

          <div className="relative z-10 flex flex-col items-center gap-2 text-center px-4">
            <div className={`w-14 h-14 rounded-2xl border bg-white/60 flex items-center justify-center mb-1 ${theme.accent} border-current/20`}>
              <theme.icon className="w-7 h-7" />
            </div>
            {brief?.theme && (
              <p className={`text-xs font-semibold ${theme.accent} bg-white/60 px-3 py-1 rounded-full border border-current/20`}>
                {brief.theme}
              </p>
            )}
            {brief?.venue_name && (
              <p className="text-[10px] text-[#706E6B] font-mono leading-snug max-w-[140px] text-center">
                {brief.venue_name}
              </p>
            )}
          </div>
        </div>

        {/* ── RIGHT: coverage ─────────────────────────────────────────────── */}
        <div className="p-5 sm:p-7 flex flex-col justify-center">
          <CoverageWidget coverage={coverage} />
        </div>
      </div>
    </div>
  );
}

// ── Coverage widget ────────────────────────────────────────────────────────────
function CoverageWidget({ coverage }) {
  const { overall, confirmedFn, totalFn, requiredSvcs, totalSvc, reqMapping } = coverage;
  const ring = overall >= 90 ? "text-[#2E844A]" : overall >= 70 ? "text-[#8C4B00]" : "text-[#706E6B]";

  return (
    <div data-testid="blueprint-coverage-widget">
      <p className="text-[9px] font-mono text-[#706E6B] uppercase tracking-widest mb-3">Blueprint Coverage</p>
      <div className="flex items-center gap-4 mb-4">
        <div className="relative w-16 h-16 flex-shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#EEF4FF" strokeWidth="2.5" />
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeDasharray={`${overall} ${100 - overall}`}
              strokeLinecap="round" className={ring} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-base font-bold leading-none ${ring}`}>{overall}%</span>
            <span className="text-[8px] text-[#706E6B] font-mono">Complete</span>
          </div>
        </div>
        <div className="space-y-1.5 min-w-0">
          <CoverageRow label="Functions" value={`${confirmedFn} / ${totalFn}`} />
          <CoverageRow label="Core Services" value={`${requiredSvcs} / ${totalSvc}`} />
          <CoverageRow label="Requirements" value={`${reqMapping}%`} />
        </div>
      </div>
      <p className="text-[9px] text-[#444444] font-mono leading-relaxed">
        Fn×0.35 + Svc×0.35 + AI confidence×0.30
      </p>
    </div>
  );
}
function CoverageRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-[10px] text-[#706E6B]">{label}</span>
      <span className="text-[10px] font-semibold text-[#444444] font-mono">{value}</span>
    </div>
  );
}

// ── Agent Summary Card ─────────────────────────────────────────────────────────
function AgentSummaryCard({ bp, brief }) {
  const indicators = [
    brief?.theme && { label: "Theme Alignment", value: "STRONG",  color: "text-[#2E844A]" },
    bp.ai_confidence >= 0.8 && { label: "Service Readiness", value: `${Math.round(bp.ai_confidence * 100)}%`, color: "text-[#0176D3]" },
    (bp.event_services || []).some(s => s.status === "REQUIRED") && { label: "Guest Experience", value: "HIGH", color: "text-[#6832B8]" },
  ].filter(Boolean);

  return (
    <div className="rounded-xl border border-[#6832B8]/20 bg-[#F6EEFE] p-5 sm:p-6" data-testid="blueprint-agent-summary">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded bg-[#6832B8]/10 border border-[#6832B8]/20 flex items-center justify-center">
          <Wand2 className="w-3.5 h-3.5 text-[#6832B8]" />
        </div>
        <p className="text-xs font-semibold text-[#6832B8] uppercase tracking-wider">Agent Summary</p>
        <span className="ml-auto text-[9px] font-mono text-[#706E6B]">Generated with Event Planning Agent</span>
      </div>
      <div className="flex flex-col sm:flex-row gap-5">
        <p className="text-sm text-[#444444] leading-relaxed flex-1" data-testid="blueprint-agent-summary-text">
          {bp.agent_summary}
        </p>
        {indicators.length > 0 && (
          <div className="flex sm:flex-col gap-3 sm:gap-4 sm:min-w-[140px] flex-wrap">
            {indicators.map((ind, i) => (
              <div key={i} className="flex flex-col gap-0.5">
                <p className="text-[9px] font-mono text-[#706E6B] uppercase tracking-wider">{ind.label}</p>
                <p className={`text-sm font-bold ${ind.color}`}>{ind.value}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Intelligence Strip ─────────────────────────────────────────────────────────
function IntelligenceStrip({ bp, event, brief }) {
  const pendingDecisions = (bp.event_services || []).filter(s => s.status === "PROPOSED").length;
  const items = [
    brief?.theme          && { label: "Theme",      value: brief.theme },
    event.guest_count     && { label: "Guests",     value: event.guest_count },
    (bp.event_functions || []).length > 0 && { label: "Functions", value: bp.event_functions.length },
    (bp.event_services || []).length > 0  && { label: "Services",  value: bp.event_services.length },
    pendingDecisions > 0  && { label: "Pending Decisions", value: pendingDecisions, alert: true },
  ].filter(Boolean);

  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, i) => (
        <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${item.alert ? "bg-[#FFF4E5] border-[#FE9339]/30 text-[#8C4B00]" : "bg-white border-[#EEF4FF] text-[#706E6B]"}`}>
          <span className="text-[#706E6B] text-[10px] uppercase tracking-wide font-mono">{item.label}</span>
          <span className={`font-semibold ${item.alert ? "text-[#8C4B00]" : "text-[#181818]"}`}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Section header ─────────────────────────────────────────────────────────────
function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] flex items-center justify-center flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-[#181818]" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
        {subtitle && <p className="text-xs text-[#706E6B] mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

// ── Function Card ──────────────────────────────────────────────────────────────
function FunctionCard({ fn, index, expanded, onToggle, services }) {
  const meta = fnMeta(fn.code);
  const Icon = meta.icon;
  const FN_COLORS = ["text-rose-600", "text-[#6832B8]", "text-[#8C4B00]", "text-[#0176D3]", "text-[#2E844A]"];
  const color = meta.color || FN_COLORS[index % FN_COLORS.length];

  return (
    <div className={`rounded-xl border ${expanded ? "border-[#0176D3]/30 bg-[#EEF4FF]" : "border-[#E5E5E5] bg-white"} overflow-hidden transition-all duration-200`}
      data-testid={`blueprint-fn-${fn.code?.toLowerCase() || index}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 hover:bg-[#F3F3F3] transition-colors">
        <div className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 ${meta.bg || "bg-[#FAFAFA] border-[#C9C9C9]"}`}>
          <Icon className={`w-4.5 h-4.5 ${color}`} style={{ width: "18px", height: "18px" }} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-semibold text-[#181818]">{fn.name}</p>
          {!expanded && fn.reason && <p className="text-xs text-[#706E6B] truncate">{fn.reason}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${fn.required ? "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30" : "bg-[#E5E5E5]/60 text-[#706E6B] border border-[#C9C9C9]"}`}>
            {fn.required ? "REQUIRED" : "OPTIONAL"}
          </span>
          {services.length > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 bg-[#FAFAFA] text-[#706E6B] rounded font-mono">{services.length} svc</span>
          )}
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-[#706E6B]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#706E6B]" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-0 space-y-3 border-t border-[#EEF4FF]">
          {fn.reason && <p className="text-xs text-[#706E6B] pt-3 leading-relaxed">{fn.reason}</p>}
          {services.length > 0 && (
            <div>
              <p className="text-[9px] font-mono text-[#706E6B] uppercase tracking-widest mb-2">Services for this function</p>
              <div className="flex flex-wrap gap-1.5">
                {services.map(s => {
                  const SIcon = svcIcon(s.code);
                  const ss = statusStyle(s.status);
                  return (
                    <span key={s.code} className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border ${ss.pill}`}>
                      <SIcon className="w-3 h-3" style={{ width: "12px", height: "12px" }} />
                      {s.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Service Row ────────────────────────────────────────────────────────────────
function ServiceRow({ svc, expanded, onToggle }) {
  const SIcon = svcIcon(svc.code);
  const ss    = statusStyle(svc.status);

  return (
    <div className={`rounded-xl border transition-all duration-200 overflow-hidden ${expanded ? "border-[#0176D3]/30 bg-[#EEF4FF]" : "border-[#E5E5E5] bg-white"}`}
      data-testid={`blueprint-svc-${svc.code?.replace("sc-", "") || svc.name?.toLowerCase()}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3.5 hover:bg-[#F3F3F3] transition-colors">
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${ss.dot === "bg-[#0176D3]" ? "bg-[#EEF4FF] border-[#0176D3]/30" : "bg-[#FAFAFA] border-[#C9C9C9]"}`}>
          <SIcon className="w-3.5 h-3.5 text-[#706E6B]" style={{ width: "14px", height: "14px" }} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-semibold text-[#181818]">{svc.name}</p>
          {!expanded && svc.reason && <p className="text-xs text-[#706E6B] truncate">{svc.reason}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ss.pill}`}>{svc.status}</span>
          {svc.source && <span className="text-[9px] font-mono text-[#706E6B] hidden sm:inline">{svc.source}</span>}
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-[#706E6B]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#706E6B]" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-[#EEF4FF] space-y-3">
          {svc.reason && <p className="text-xs text-[#706E6B] pt-3 leading-relaxed">{svc.reason}</p>}
          <div className="flex flex-wrap gap-3 text-xs text-[#706E6B]">
            {svc.source && <span className="font-mono">Source: <span className="text-[#706E6B]">{svc.source}</span></span>}
            {svc.confidence > 0 && <span className="font-mono">Confidence: <span className="text-[#6832B8]">{Math.round(svc.confidence * 100)}%</span></span>}
          </div>
          {svc.status === "PROPOSED" && (
            <div className="flex gap-2 pt-1">
              <button className="px-3 py-1.5 text-xs bg-[#EAF5EE] text-[#2E844A] border border-[#2E844A]/20 rounded-lg hover:bg-[#EAF5EE] transition-colors">Accept</button>
              <button className="px-3 py-1.5 text-xs bg-[#FAFAFA] text-[#706E6B] border border-[#C9C9C9] rounded-lg hover:bg-[#E5E5E5] transition-colors">Modify</button>
              <button className="px-3 py-1.5 text-xs bg-[#FCEBEC] text-[#BA0517]/80 border border-[#BA0517]/20 rounded-lg hover:bg-[#FCEBEC] transition-colors">Exclude</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Missing Information Card ───────────────────────────────────────────────────
function MissingInfoCard({ items, eventId, navigate }) {
  return (
    <div className="rounded-xl border border-[#FE9339]/30 bg-[#FFF4E5] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Info className="w-4 h-4 text-[#8C4B00] flex-shrink-0" />
        <p className="text-xs font-semibold text-[#8C4B00] uppercase tracking-wider">Needs Clarification</p>
      </div>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-xs text-amber-500">
            <span className="w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />
            {item}
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={() => navigate && navigate(`/imagine/events/${eventId}?tab=brief`)}
          className="px-3 py-1.5 text-xs bg-[#FFF4E5] text-[#8C4B00] border border-[#FE9339]/30 rounded-lg hover:bg-[#FFF4E5] transition-colors">
          Update Brief
        </button>
      </div>
    </div>
  );
}

// ── Architecture Tree ──────────────────────────────────────────────────────────
function ArchitectureTree({ functions, services }) {
  const [open, setOpen] = useState(false);
  const tree = functions.map(fn => ({
    ...fn,
    linked: services.filter(s => s.applies_to_functions?.includes(fn.code)),
  }));

  return (
    <div className="rounded-xl border border-[#EEF4FF] bg-white overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 p-4 hover:bg-[#F3F3F3] transition-colors">
        <GitBranch className="w-4 h-4 text-[#706E6B]" />
        <span className="text-sm font-medium text-[#444444] flex-1 text-left">Event Architecture</span>
        <span className="text-[10px] text-[#706E6B] font-mono mr-2">Function → Service map</span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-[#706E6B]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#706E6B]" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-[#EEF4FF] space-y-4 font-mono text-xs">
          <div className="text-[#706E6B] pl-2 pb-1 border-l border-[#E5E5E5]">EVENT</div>
          {tree.map((fn, i) => {
            const meta = fnMeta(fn.code);
            const Icon = meta.icon;
            return (
              <div key={fn.code || i} className="pl-4 border-l border-[#E5E5E5]">
                <div className="flex items-center gap-1.5 text-[#444444] mb-1.5">
                  <span className="text-[#444444]">├─</span>
                  <Icon className={`w-3 h-3 ${meta.color}`} style={{ width: "12px", height: "12px" }} />
                  <span className="font-semibold">{fn.name}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${fn.required ? "text-[#0176D3] bg-[#EEF4FF]" : "text-[#706E6B] bg-[#FAFAFA]"}`}>
                    {fn.required ? "REQ" : "OPT"}
                  </span>
                </div>
                <div className="pl-5 space-y-1">
                  {fn.linked.map((s, si) => {
                    const SIcon = svcIcon(s.code);
                    const ss = statusStyle(s.status);
                    return (
                      <div key={s.code || si} className="flex items-center gap-1.5 text-[#706E6B]">
                        <span className="text-[#444444]">{si < fn.linked.length - 1 ? "├─" : "└─"}</span>
                        <SIcon className="w-3 h-3" style={{ width: "11px", height: "11px" }} />
                        <span>{s.name}</span>
                        <span className={`text-[8px] ml-auto ${ss.dot.replace("bg-", "text-").replace("[#0176D3]", "[#0176D3]").replace("emerald-400", "emerald-400").replace("violet-400", "violet-400")}`}>
                          {s.status}
                        </span>
                      </div>
                    );
                  })}
                  {fn.linked.length === 0 && <div className="text-[#444444] pl-4">└─ (no services mapped)</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Blueprint Actions Bar ─────────────────────────────────────────────────────
function BlueprintActionsBar({ bp, event, onGenerate, onApprove, generating, approving, navigate }) {
  const isApproved = bp.status === "APPROVED";
  const isDraft    = bp.status === "DRAFT";
  const isReview   = bp.status === "REVIEW_REQUIRED";

  return (
    <div className="flex flex-wrap gap-3 items-center pt-1 border-t border-[#EEF4FF]">
      {/* POC decision actions */}
      {!isApproved && (
        <button data-testid="approve-blueprint-btn" onClick={onApprove} disabled={approving}
          className="flex items-center gap-2 px-4 py-2 bg-[#EAF5EE] text-[#2E844A] border border-[#2E844A]/30 rounded-xl text-sm font-medium hover:bg-[#EAF5EE] transition-colors disabled:opacity-50">
          {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Approve Blueprint
        </button>
      )}
      {isApproved && (
        <div className="flex items-center gap-2 px-4 py-2 bg-[#EAF5EE] text-[#2E844A] border border-[#2E844A]/20 rounded-xl text-sm">
          <Lock className="w-4 h-4" /> Blueprint Approved
        </div>
      )}

      <button data-testid="regenerate-blueprint-btn" onClick={onGenerate} disabled={generating}
        className="flex items-center gap-2 px-4 py-2 bg-[#FAFAFA] text-[#444444] border border-[#C9C9C9] rounded-xl text-sm font-medium hover:bg-[#E5E5E5] transition-colors disabled:opacity-50">
        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        {isApproved ? "Create New Revision" : "Re-run Analysis"}
      </button>

      <div className="ml-auto flex gap-2">
        {/* Event graph / thread bridge */}
        {event.planning_stage === "ACTIVATED" && event.threads_activated && (
          <button onClick={() => navigate(`/events/${event.id}/threads`)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#0176D3] border border-[#0176D3]/30 rounded-xl hover:bg-[#EEF4FF] transition-colors">
            <Network className="w-3.5 h-3.5" />
            <span className="font-medium">Operational Threads</span>
            <span className="bg-[#EEF4FF] text-[#0176D3] text-[9px] px-1.5 py-0.5 rounded-full font-mono">Active</span>
            <ArrowUpRight className="w-3 h-3" />
          </button>
        )}
        {event.planning_stage === "APPROVED" && !event.threads_activated && (
          <div className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#8C4B00] border border-[#FE9339]/30 rounded-xl bg-[#FFF4E5]">
            <TrendingUp className="w-3.5 h-3.5" />
            Event Graph · Ready to Activate
          </div>
        )}
      </div>
    </div>
  );
}

// ── Blueprint status badge ─────────────────────────────────────────────────────
function BlueprintStatusBadge({ status }) {
  const s = {
    APPROVED:        "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/30",
    REVIEW_REQUIRED: "bg-[#FFF4E5] text-[#8C4B00] border-amber-700/30",
    DRAFT:           "bg-[#F3F3F3] text-[#706E6B] border-[#C9C9C9]",
  }[status] || "bg-[#FAFAFA] text-[#706E6B] border-[#C9C9C9]";
  return (
    <span data-testid="blueprint-status-badge" className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${s}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────
function StageBadge({ stage }) {
  const colors = { DRAFT: "text-[#706E6B] bg-[#FAFAFA]", DISCOVERY: "text-[#0176D3] bg-[#EEF4FF]", BRIEF_READY: "text-[#0176D3] bg-[#EEF4FF]", BLUEPRINT_PROPOSED: "text-[#5E4DB2] bg-[#F1ECFF]", REVIEW_REQUIRED: "text-[#8C4B00] bg-[#FFF4E5]", APPROVED: "text-[#2E844A] bg-[#EAF5EE]", ACTIVATED: "text-[#0176D3] bg-[#EEF4FF]" };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wide ${colors[stage] || "text-[#706E6B] bg-[#FAFAFA]"}`}>{stage?.replace(/_/g, " ")}</span>;
}

function PlanningJourney({ stage, threadsActivated }) {
  const steps = ["DRAFT", "BRIEF_READY", "BLUEPRINT_PROPOSED", "APPROVED", "ACTIVATED"];
  const currentIdx = steps.indexOf(stage);
  return (
    <div className="flex items-center gap-0 overflow-x-auto pb-1">
      {steps.map((s, i) => {
        const done = i < currentIdx; const active = s === stage;
        return (
          <div key={s} className="flex items-center flex-1 min-w-0">
            <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all flex-shrink-0 ${done || active ? "border-[#0176D3] bg-[#EEF4FF] text-[#0176D3]" : "border-[#C9C9C9] bg-transparent text-[#706E6B]"}`}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-[9px] uppercase tracking-wider font-medium text-center whitespace-nowrap ${active || done ? "text-[#0176D3]" : "text-[#706E6B]"}`}>{s.replace(/_/g, " ")}</span>
            </div>
            {i < steps.length - 1 && <div className={`h-px flex-1 mx-1 min-w-[8px] ${done ? "bg-[#0176D3]/30" : "bg-[#FAFAFA]"}`} />}
          </div>
        );
      })}
      {threadsActivated && (
        <div className="flex items-center flex-1 min-w-0">
          <div className="h-px flex-1 mx-1 bg-[#0176D3]/40 min-w-[8px]" />
          <div className="flex flex-col items-center gap-1"><div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 border-[#0176D3] bg-[#EEF4FF] text-[#0176D3] flex-shrink-0"><CheckCircle2 className="w-4 h-4" /></div><span className="text-[9px] uppercase tracking-wider font-medium text-[#0176D3] whitespace-nowrap">THREADS</span></div>
        </div>
      )}
    </div>
  );
}

function BriefSection({ title, children }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
      <h3 className="text-xs font-semibold text-[#706E6B] uppercase tracking-wide mb-3">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function BriefRow({ label, field, form, setForm, editing, type }) {
  const val = form[field];
  if (!editing) return <div className="flex items-center justify-between gap-2"><span className="text-xs text-[#706E6B]">{label}</span><span className="text-xs text-[#181818] font-medium text-right">{val === null || val === undefined ? "—" : type === "bool" ? (val ? "Yes" : "No") : String(val)}</span></div>;
  if (type === "bool") return <div className="flex items-center justify-between gap-2"><span className="text-xs text-[#706E6B]">{label}</span><select value={val === true ? "true" : val === false ? "false" : ""} onChange={e => setForm(f => ({ ...f, [field]: e.target.value === "true" ? true : e.target.value === "false" ? false : null }))} className="bg-white border border-[#C9C9C9] rounded text-xs text-[#181818] px-2 py-1"><option value="">—</option><option value="true">Yes</option><option value="false">No</option></select></div>;
  return <div className="flex items-center justify-between gap-2"><span className="text-xs text-[#706E6B]">{label}</span><input type={type || "text"} value={val || ""} onChange={e => setForm(f => ({ ...f, [field]: type === "number" ? parseInt(e.target.value) || null : e.target.value }))} className="bg-white border border-[#C9C9C9] rounded text-xs text-[#181818] px-2 py-1 w-32 text-right" /></div>;
}
function ActionButton({ label, icon, onClick, loading: isLoading, variant = "primary", testId }) {
  const styles = { primary: "bg-[#0176D3] text-white hover:opacity-90", secondary: "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30 hover:bg-[#EEF4FF]", ghost: "bg-[#FAFAFA] text-[#444444] hover:bg-[#E5E5E5]" };
  return <button data-testid={testId} onClick={onClick} disabled={isLoading} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 ${styles[variant]}`}>{isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}{label}</button>;
}
